import { useContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import i18nContext from 'components/i18n-context';
import NotesTab from 'components/NotesTab';
import Loader from 'components/Loader';
import { toast } from 'react-toastify';
import RequestDocumentModal from '../RequestDocumentModal';
import DocumentRequestItem from '../DocumentRequestItem';
import AttachDocumentModal from './components/AttachDocumentModal';
import ConfirmationModal from 'components/PopUpScheme/ConfirmationModal';
import { ReactComponent as DocumentPointIcon } from 'assets/document-point-icon.svg';
import { ReactComponent as DeleteIcon } from 'assets/iconButtons/delete-new-icon.svg';
import { ReactComponent as EditIcon } from 'assets/iconButtons/edit-new-icon.svg';
import {
  getDocumentRequestsRequest,
  getDocumentRequestByIdRequest,
  createDocumentRequestRequest,
  completeDocumentRequestRequest,
  cancelDocumentRequestRequest,
  resendDocumentRequestRequest,
  downloadDocumentRequestFileRequest,
  attachPaymentTransactionDocumentRequest
} from 'services/requestAgent';
import { formatDateTime } from 'services/utils';
import Button from 'uikit/Button/Button';
import { IconButton } from 'uikit/IconButton/IconButton';
import './DocumentsTab.scss';

// Mapping of document types to their i18n label keys
const DOCUMENT_TYPE_LABELS = {
  INVOICE: 'transactionDetails.documentRequest.type.invoice',
  AGREEMENT: 'transactionDetails.documentRequest.type.agreement',
  TRANSPORTATION_DOCUMENTS: 'transactionDetails.documentRequest.type.transportationDocuments',
  PROOF_OF_WORK: 'transactionDetails.documentRequest.type.proofOfWork',
  BANK_STATEMENT: 'transactionDetails.documentRequest.type.bankStatement',
  TAX_DECLARATION: 'transactionDetails.documentRequest.type.taxDeclaration',
  IDENTIFICATION_DOCUMENT: 'transactionDetails.documentRequest.type.identificationDocument',
  PROOF_OF_ADDRESS: 'transactionDetails.documentRequest.type.proofOfAddress',
  PROOF_OF_DIVIDENDS: 'transactionDetails.documentRequest.type.proofOfDividends',
  PURPOSE_OF_PAYMENT: 'transactionDetails.documentRequest.type.purposeOfPayment',
  OTHER: 'transactionDetails.documentRequest.type.other'
};

const DocumentIcon = () => (
  <svg width='64' height='59' viewBox='0 0 64 59' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M58.2438 2C59.2992 2 60.2548 2.41801 60.9464 3.09383C61.6381 3.76966 62.0659 4.7033 62.0659 5.73457V40.2075C62.0659 41.2388 61.6381 42.1725 60.9464 42.8483C60.2548 43.5241 59.2992 43.9421 58.2438 43.9421H5.32212C4.26667 43.9421 3.31114 43.5241 2.61947 42.8483C1.92781 42.1725 1.5 41.2388 1.5 40.2075V5.73457C1.5 4.7033 1.92781 3.76966 2.61947 3.09383C3.31114 2.41801 4.26667 2 5.32212 2H58.2438Z'
      stroke='#D2DAE4'
      strokeWidth='2'
    />
    <path d='M54.4225 12.918H23.5859' stroke='#D2DAE4' strokeWidth='2' strokeLinecap='round' />
    <path d='M35.6059 26.7051H23.5859' stroke='#D2DAE4' strokeWidth='2' strokeLinecap='round' />
    <path d='M38.546 19.8125H23.5859' stroke='#D2DAE4' strokeWidth='2' strokeLinecap='round' />
    <path d='M17.5867 12.918H8.85938' stroke='#D2DAE4' strokeWidth='2' strokeLinecap='round' />
    <path d='M17.5867 26.7051H8.85938' stroke='#D2DAE4' strokeWidth='2' strokeLinecap='round' />
    <path d='M17.5867 19.8125H8.85938' stroke='#D2DAE4' strokeWidth='2' strokeLinecap='round' />
    <path d='M17.5867 33.6016H8.85938' stroke='#D2DAE4' strokeWidth='2' strokeLinecap='round' />
    <path d='M35.6059 33.6016H23.5859' stroke='#D2DAE4' strokeWidth='2' strokeLinecap='round' />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M31.8637 17.3037C36.1674 17.3037 40.0639 18.9761 42.8842 21.6809C45.7026 24.3837 47.4462 28.1174 47.4462 32.242C47.4462 36.3666 45.7026 40.1003 42.8842 42.8031C40.0639 45.5078 36.1674 47.1803 31.8637 47.1803C27.56 47.1803 23.6636 45.5078 20.8432 42.8031C18.0249 40.1003 16.2812 36.3666 16.2812 32.242C16.2812 28.1174 18.0249 24.3837 20.8432 21.6809C23.6636 18.9761 27.56 17.3037 31.8637 17.3037Z'
      fill='white'
      stroke='#D2DAE4'
      strokeWidth='2'
    />
    <path
      d='M34.5099 47.233V54.3743C34.5099 55.8246 33.3342 57.0003 31.8838 57.0003C30.4335 57.0003 29.2578 55.8246 29.2578 54.3743V47.0117'
      stroke='#D2DAE4'
      strokeWidth='2'
      strokeLinecap='round'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M31.8645 22.4756C34.7072 22.4756 37.281 23.5695 39.1439 25.3392C41.0038 27.106 42.1549 29.5466 42.1549 32.2429C42.1549 34.9392 41.0038 37.3799 39.1439 39.1467C37.281 40.9164 34.7072 42.0103 31.8645 42.0103C29.0219 42.0103 26.4481 40.9164 24.5852 39.1467C22.7253 37.3799 21.5742 34.9392 21.5742 32.2429C21.5742 29.5466 22.7253 27.106 24.5852 25.3392C26.4481 23.5695 29.0219 22.4756 31.8645 22.4756Z'
      stroke='#D2DAE4'
      strokeWidth='2'
    />
  </svg>
);

const DocumentsTab = ({
  isLoading,
  transactionNumber,
  transactionData,
  notes,
  handleLoadNotes,
  handleAddNote,
  handleDeleteNote,
  handleDownloadDocument,
  downloadTransactionDocument,
  deleteTransactionDocument,
  updateTransactionDocument,
  operatorsStore
}) => {
  const i18n = useContext(i18nContext);
  const [documentRequests, setDocumentRequests] = useState([]);
  const [documentRequestLinks, setDocumentRequestLinks] = useState({});
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [isLoadingOperators, setIsLoadingOperators] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAttachModalOpen, setIsAttachModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);
  const [error, setError] = useState(null);

  // Fetch document requests when component mounts
  useEffect(() => {
    if (transactionNumber) {
      fetchDocumentRequests();
    }
  }, [transactionNumber]);

  // Fetch operators list when component mounts
  useEffect(() => {
    const fetchOperators = async () => {
      if (operatorsStore && !operatorsStore.operatorsList.length) {
        setIsLoadingOperators(true);
        try {
          await operatorsStore.getOperatorsList();
        } finally {
          setIsLoadingOperators(false);
        }
      }
    };
    fetchOperators();
  }, [operatorsStore]);

  const fetchDocumentRequests = async () => {
    setIsLoadingRequests(true);
    try {
      // First get the list of document requests
      const requestsList = await getDocumentRequestsRequest(transactionNumber);

      if (requestsList && requestsList.length > 0) {
        // Fetch detailed information for each request
        const detailedRequests = await Promise.all(
          requestsList.map(async (request) => {
            try {
              const detailedData = await getDocumentRequestByIdRequest(request.id);
              return detailedData;
            } catch (err) {
              console.error(`Failed to fetch details for request ${request.id}:`, err);
              // Return the basic request data if detailed fetch fails
              return request;
            }
          })
        );

        // Keep the backend order: requests come sorted from newest to oldest
        setDocumentRequests(detailedRequests);
      } else {
        setDocumentRequests([]);
      }
    } catch (err) {
      console.error('Failed to fetch document requests:', err);
      setError(err.message || 'Failed to load document requests');
    } finally {
      setIsLoadingRequests(false);
    }
  };

  const handleRequestDocument = () => {
    // Check if there's already an active request
    const activeRequest = documentRequests.find((req) => req.status === 'REQUESTED' || req.status === 'PROVIDED');

    if (activeRequest) {
      setError(i18n.getMessage('transactionDetails.documentRequest.error.activeRequestExists'));
      return;
    }

    setIsModalOpen(true);
  };

  const handleCreateRequest = async (items) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await createDocumentRequestRequest(transactionNumber, items);
      // Save the link if it exists in the response
      if (response && response.link) {
        setDocumentRequestLinks((prev) => ({
          ...prev,
          [response.id]: response.link
        }));
      }
      await fetchDocumentRequests();
      setIsModalOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to create document request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompleteRequest = (requestId) => {
    setCompletingRequestId(requestId);
    setCompleteRequestModalOpen(true);
  };

  const handleConfirmCompleteRequest = async () => {
    if (!completingRequestId) return;

    setIsSubmitting(true);
    try {
      await completeDocumentRequestRequest(completingRequestId);
      await fetchDocumentRequests();
      toast.success(i18n.getMessage('transactionDetails.documentRequest.completeSuccess'));
    } catch (err) {
      console.error('Failed to complete document request:', err);
      setError(err.message || 'Failed to complete document request');
      toast.error(i18n.getMessage('transactionDetails.documentRequest.completeError'));
    } finally {
      setIsSubmitting(false);
      setCompleteRequestModalOpen(false);
      setCompletingRequestId(null);
    }
  };

  const handleCancelRequest = (requestId) => {
    setDeletingRequestId(requestId);
    setDeleteRequestModalOpen(true);
  };

  const handleConfirmDeleteRequest = async () => {
    if (!deletingRequestId) return;

    setIsSubmitting(true);
    try {
      await cancelDocumentRequestRequest(deletingRequestId);
      await fetchDocumentRequests();
      toast.success(i18n.getMessage('transactionDetails.documentRequest.cancelSuccess'));
    } catch (err) {
      console.error('Failed to cancel document request:', err);
      setError(err.message || 'Failed to cancel document request');
      toast.error(i18n.getMessage('transactionDetails.documentRequest.cancelError'));
    } finally {
      setIsSubmitting(false);
      setDeleteRequestModalOpen(false);
      setDeletingRequestId(null);
    }
  };

  const handleResendRequest = (requestId) => {
    setResendingRequestId(requestId);
    setResendRequestModalOpen(true);
  };

  const handleConfirmResendRequest = async () => {
    if (!resendingRequestId) return;

    setIsSubmitting(true);
    try {
      const response = await resendDocumentRequestRequest(resendingRequestId);
      // Update the link if it exists in the response
      if (response && response.link) {
        setDocumentRequestLinks((prev) => ({
          ...prev,
          [resendingRequestId]: response.link
        }));
      }
      toast.success(i18n.getMessage('transactionDetails.documentRequest.resendSuccess'));
    } catch (err) {
      setError(err.message || 'Failed to resend document request');
      toast.error(i18n.getMessage('transactionDetails.documentRequest.resendError'));
    } finally {
      setIsSubmitting(false);
      setResendRequestModalOpen(false);
      setResendingRequestId(null);
    }
  };

  const [editingDocument, setEditingDocument] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);
  const [deletingDocumentName, setDeletingDocumentName] = useState(null);

  // States for document request deletion
  const [deleteRequestModalOpen, setDeleteRequestModalOpen] = useState(false);
  const [deletingRequestId, setDeletingRequestId] = useState(null);

  // States for document request completion
  const [completeRequestModalOpen, setCompleteRequestModalOpen] = useState(false);
  const [completingRequestId, setCompletingRequestId] = useState(null);

  // States for document request resend
  const [resendRequestModalOpen, setResendRequestModalOpen] = useState(false);
  const [resendingRequestId, setResendingRequestId] = useState(null);

  const handleAttachDocument = () => {
    setEditingDocument(null);
    setIsAttachModalOpen(true);
  };

  const handleEditDocument = (document) => {
    setEditingDocument(document);
    setIsAttachModalOpen(true);
  };

  const handleUploadDocument = async (data) => {
    setIsUploadingDocument(true);
    try {
      // Check if we're editing an existing document
      if (editingDocument && editingDocument.id) {
        // UPDATE existing document - only send metadata, no file
        const updateData = {
          hidden: data.isHidden,
          category: data.category,
          note: data.note || ''
        };

        try {
          // Use the store method to update the document
          // The store will handle updating the local state automatically
          await updateTransactionDocument(editingDocument.id, updateData);
          toast.success(i18n.getMessage('transactionDetails.documents.updateSuccess'));
        } catch (err) {
          console.error('Failed to update document:', err);
          throw err;
        }
      } else {
        // CREATE new document(s) - send file(s) with metadata
        // Process files one by one as backend expects single file per request
        const newDocuments = [];
        for (const file of data.files) {
          // Skip non-File objects (like preview objects for editing)
          if (!(file instanceof File)) {
            continue;
          }

          const formData = new FormData();

          // Create DTO as JSON Blob
          const dto = {
            hidden: data.isHidden,
            category: data.category,
            note: data.note || ''
          };

          formData.append('dto', new Blob([JSON.stringify(dto)], { type: 'application/json' }));
          formData.append('file', file);

          try {
            const newDocument = await attachPaymentTransactionDocumentRequest(transactionNumber, formData);
            if (newDocument) {
              newDocuments.push(newDocument);
            }
          } catch (err) {
            console.error('Failed to upload document:', err);
            throw err;
          }
        }

        // Add new documents to transactionData
        if (transactionData && transactionData.documents && newDocuments.length > 0) {
          transactionData.documents = [...transactionData.documents, ...newDocuments];
        }

        toast.success(i18n.getMessage('transactionDetails.documents.uploadSuccess'));
      }

      // No page reload - data is updated locally
    } finally {
      setIsUploadingDocument(false);
      setEditingDocument(null);
    }
  };

  // Helper function to get operator name by ID
  const getOperatorName = (operatorId) => {
    if (!operatorId || !operatorsStore || !operatorsStore.operatorsList) {
      return operatorId; // Return ID if no name found
    }
    const operator = operatorsStore.operatorsList.find((op) => op.id === operatorId);
    return operator ? operator.name : operatorId;
  };

  const handleDownloadAttachment = async (documentId) => {
    try {
      await downloadTransactionDocument(documentId);
    } catch (err) {
      console.error('Failed to download document:', err);
      toast.error('Failed to download document');
    }
  };

  const handleDeleteAttachment = async (documentId, documentName) => {
    setDeletingDocumentId(documentId);
    setDeletingDocumentName(documentName);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingDocumentId) return;

    setIsDeletingDocument(true);
    try {
      await deleteTransactionDocument(deletingDocumentId);
      // The store will automatically update the local documents array
      toast.success(i18n.getMessage('transactionDetails.documents.deleteSuccess'));
    } catch (err) {
      console.error('Failed to delete document:', err);
      toast.error(i18n.getMessage('transactionDetails.documents.deleteError'));
    } finally {
      setIsDeletingDocument(false);
      setDeleteModalOpen(false);
      setDeletingDocumentId(null);
      setDeletingDocumentName(null);
    }
  };

  // Modal configurations
  const modalConfigs = {
    deleteDocument: {
      isOpen: deleteModalOpen,
      onClose: () => {
        setDeleteModalOpen(false);
        setDeletingDocumentId(null);
        setDeletingDocumentName(null);
      },
      onConfirm: handleConfirmDelete,
      isLoading: isDeletingDocument,
      title: i18n.getMessage('transactionDetails.documents.deleteModal.title'),
      description: i18n.getMessage('transactionDetails.documents.deleteModal.description'),
      cancelButtonText: i18n.getMessage('transactionDetails.documents.deleteModal.cancel'),
      confirmButtonText: i18n.getMessage('transactionDetails.documents.deleteModal.confirm'),
      type: 'delete',
      additionalContent: deletingDocumentName && (
        <span className='confirmation-modal__filename'> &ldquo;{deletingDocumentName}&rdquo;</span>
      )
    },
    deleteRequest: {
      isOpen: deleteRequestModalOpen,
      onClose: () => {
        setDeleteRequestModalOpen(false);
        setDeletingRequestId(null);
      },
      onConfirm: handleConfirmDeleteRequest,
      isLoading: isSubmitting,
      title: i18n.getMessage('transactionDetails.documentRequest.deleteModal.title'),
      description: i18n.getMessage('transactionDetails.documentRequest.deleteModal.description'),
      cancelButtonText: i18n.getMessage('transactionDetails.documentRequest.deleteModal.cancel'),
      confirmButtonText: i18n.getMessage('transactionDetails.documentRequest.deleteModal.confirm'),
      type: 'delete'
    },
    completeRequest: {
      isOpen: completeRequestModalOpen,
      onClose: () => {
        setCompleteRequestModalOpen(false);
        setCompletingRequestId(null);
      },
      onConfirm: handleConfirmCompleteRequest,
      isLoading: isSubmitting,
      title: i18n.getMessage('transactionDetails.documentRequest.completeModal.title'),
      description: i18n.getMessage('transactionDetails.documentRequest.completeModal.description'),
      cancelButtonText: i18n.getMessage('transactionDetails.documentRequest.completeModal.cancel'),
      confirmButtonText: i18n.getMessage('transactionDetails.documentRequest.completeModal.confirm'),
      type: 'confirm'
    },
    resendRequest: {
      isOpen: resendRequestModalOpen,
      onClose: () => {
        setResendRequestModalOpen(false);
        setResendingRequestId(null);
      },
      onConfirm: handleConfirmResendRequest,
      isLoading: isSubmitting,
      title: i18n.getMessage('transactionDetails.documentRequest.resendModal.title'),
      description: i18n.getMessage('transactionDetails.documentRequest.resendModal.description'),
      cancelButtonText: i18n.getMessage('transactionDetails.documentRequest.resendModal.cancel'),
      confirmButtonText: i18n.getMessage('transactionDetails.documentRequest.resendModal.confirm'),
      type: 'confirm'
    }
  };

  // Get the latest/active document request
  const activeRequest =
    documentRequests.find((req) => req.status === 'REQUESTED' || req.status === 'PROVIDED') || documentRequests[0];

  // Get documents from transaction data
  const allDocuments = transactionData?.documents || [];

  // Calculate attached documents with proper memoization
  // This will recalculate when documentRequests or allDocuments change
  const attachedDocuments = useMemo(() => {
    // Collect all file IDs from Document Requests
    const documentRequestFileIds = new Set();

    // Only process if documentRequests have been loaded
    if (documentRequests && documentRequests.length > 0) {
      documentRequests.forEach((request) => {
        if (request.items && Array.isArray(request.items)) {
          request.items.forEach((item) => {
            if (item.files && Array.isArray(item.files)) {
              item.files.forEach((file) => {
                if (file.id) {
                  documentRequestFileIds.add(file.id);
                }
              });
            }
          });
        }
      });
    }

    // Filter out documents that were uploaded via Document Request
    // Exclude if ID exists in Document Request files
    return allDocuments.filter((doc) => !documentRequestFileIds.has(doc.id));
  }, [documentRequests, allDocuments]);

  // Check if all data is loaded
  const isDataLoading = isLoadingRequests || isLoadingOperators;

  return (
    <div className={'documents-tab-wrapper'}>
      {isDataLoading ? (
        <Loader className={'documents-tab-loader'} />
      ) : (
        <div className={'documents-tab-content'}>
          {/* Document Requests Section */}
          <div className={'document-section'}>
            <div className={'document-section-header'}>
              <span>{i18n.getMessage('transactionDetails.documentsTab.documentRequests')}</span>
              {documentRequests.length > 0 && (
                <Button
                  className={'document-action-button'}
                  type={'primary'}
                  size={'small'}
                  onClick={handleRequestDocument}
                  disabled={
                    isSubmitting ||
                    (activeRequest && (activeRequest.status === 'REQUESTED' || activeRequest.status === 'PROVIDED'))
                  }
                >
                  {i18n.getMessage('transactionDetails.documentsTab.requestDocument')}
                </Button>
              )}
            </div>
            <div className={'document-section-body'}>
              {documentRequests.length === 0 ? (
                <div className={'document-placeholder'}>
                  <div className={'placeholder-content'}>
                    <DocumentIcon />
                    <div className={'placeholder-text'}>
                      <h3>{i18n.getMessage('transactionDetails.documentsTab.noRequestsYet')}</h3>
                      <p>{i18n.getMessage('transactionDetails.documentsTab.noRequestsDescription')}</p>
                    </div>
                  </div>
                  <Button
                    className={'document-action-button'}
                    type={'primary'}
                    size={'small'}
                    onClick={handleRequestDocument}
                    disabled={isSubmitting}
                  >
                    {i18n.getMessage('transactionDetails.documentsTab.requestDocument')}
                  </Button>
                </div>
              ) : (
                <div className={'document-requests-list'}>
                  {(() => {
                    // Find the last request with REQUESTED or PROVIDED status
                    let lastActiveIndex = -1;
                    for (let i = documentRequests.length - 1; i >= 0; i--) {
                      if (documentRequests[i].status === 'REQUESTED' || documentRequests[i].status === 'PROVIDED') {
                        lastActiveIndex = i;
                        break;
                      }
                    }

                    return documentRequests.map((request, index) => (
                      <DocumentRequestItem
                        key={request.id}
                        request={request}
                        requestLink={documentRequestLinks[request.id]}
                        onComplete={handleCompleteRequest}
                        onCancel={handleCancelRequest}
                        onResend={handleResendRequest}
                        onDownload={(fileId) => handleDownloadAttachment(fileId)}
                        isLoading={isSubmitting}
                        initialExpanded={index === lastActiveIndex}
                      />
                    ));
                  })()}
                </div>
              )}
              {error && <div className={'error-message'}>{error}</div>}
            </div>
          </div>

          {/* Document Attachments Section */}
          <div className={'document-section'}>
            <div className={'document-section-header'}>
              <span>{i18n.getMessage('transactionDetails.documentsTab.documentAttachments')}</span>
              {attachedDocuments.length > 0 && (
                <Button
                  className={'document-action-button'}
                  type={'primary'}
                  size={'small'}
                  onClick={handleAttachDocument}
                  disabled={isUploadingDocument}
                >
                  {i18n.getMessage('transactionDetails.documentsTab.attachDocument')}
                </Button>
              )}
            </div>
            <div className={'document-section-body'}>
              {attachedDocuments.length === 0 ? (
                <div className={'document-placeholder'}>
                  <div className={'placeholder-content'}>
                    <DocumentIcon />
                    <div className={'placeholder-text'}>
                      <h3>{i18n.getMessage('transactionDetails.documentsTab.noAttachmentsYet')}</h3>
                      <p>{i18n.getMessage('transactionDetails.documentsTab.noAttachmentsDescription')}</p>
                    </div>
                  </div>
                  <Button
                    className={'document-action-button'}
                    type={'primary'}
                    size={'small'}
                    onClick={handleAttachDocument}
                    disabled={isUploadingDocument}
                  >
                    {i18n.getMessage('transactionDetails.documentsTab.attachDocument')}
                  </Button>
                </div>
              ) : (
                <div className={'document-attachments-list'}>
                  <div className={'items-list'}>
                    {attachedDocuments.map((doc) => (
                      <div key={doc.id} className={'request-item'}>
                        <div className={'item-icon-wrapper'}>
                          <DocumentPointIcon className={'item-icon'} />
                        </div>
                        <div className={'item-content'}>
                          <div className={'item-info-wrapper'}>
                            <div className={'item-info'}>
                              <span className={'item-type'}>
                                {DOCUMENT_TYPE_LABELS[doc.category]
                                  ? i18n.getMessage(DOCUMENT_TYPE_LABELS[doc.category])
                                  : doc.category}
                              </span>
                              {doc.hidden && <span className={'hidden-badge'}>Hidden</span>}
                            </div>
                            <div className={'files-list'}>
                              <div className={'file-item'}>
                                <span
                                  className={'file-name clickable'}
                                  onClick={() => handleDownloadAttachment(doc.id)}
                                  role='button'
                                  tabIndex={0}
                                  onKeyDown={(e) => e.key === 'Enter' && handleDownloadAttachment(doc.id)}
                                >
                                  {doc.name}
                                </span>
                                <span className={'file-size'}>({Math.round(doc.size / 1024)} KB)</span>
                              </div>
                            </div>
                            {doc.note && (
                              <div className={'item-comment'}>
                                <span className={'comment-label'}>
                                  {i18n.getMessage('transactionDetails.documentRequest.commentLabel')}
                                </span>
                                <span className={'comment-value'}>{doc.note}</span>
                              </div>
                            )}
                            {doc?.uploadDate && (
                              <div className={'item-comment'}>
                                <span className='comment-label'>
                                  {i18n.getMessage('transactionDetails.documentRequest.uploadDateLabel')}
                                </span>
                                <span className={'comment-value'}>{formatDateTime(doc.uploadDate)}</span>
                              </div>
                            )}
                            <div className={'item-comment'}>
                              <span className='comment-label'>
                                {i18n.getMessage('transactionDetails.documentRequest.operatorLabel')}
                              </span>
                              <span className={'comment-value'}>
                                {doc?.operatorId
                                  ? getOperatorName(doc?.operatorId)
                                  : i18n.getMessage('transactionDetails.documentRequest.byCustomer')}
                              </span>
                            </div>
                          </div>

                          <div className={'item-actions-wrapper'}>
                            <IconButton
                              className={'item-action-button'}
                              Icon={EditIcon}
                              onClick={() => handleEditDocument(doc)}
                            />
                            <IconButton
                              className={'item-action-button'}
                              Icon={DeleteIcon}
                              onClick={() => handleDeleteAttachment(doc.id, doc.name)}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!isDataLoading && (
        <NotesTab
          isLoading={isLoading}
          notes={notes}
          transactionNumber={transactionNumber}
          handleLoadNotes={handleLoadNotes}
          handleAddNote={handleAddNote}
          handleDeleteNote={handleDeleteNote}
          handleDownloadDocument={handleDownloadDocument}
        />
      )}

      {/* Request Document Modal */}
      {!isDataLoading && (
        <RequestDocumentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleCreateRequest}
          isLoading={isSubmitting}
        />
      )}

      {/* Attach Document Modal */}
      {!isDataLoading && (
        <AttachDocumentModal
          isOpen={isAttachModalOpen}
          onClose={() => {
            setIsAttachModalOpen(false);
            setEditingDocument(null);
          }}
          onUpload={handleUploadDocument}
          isLoading={isUploadingDocument}
          editingDocument={editingDocument}
          onDownload={handleDownloadAttachment}
        />
      )}

      {/* Confirmation Modals */}
      {!isDataLoading && (
        <>
          <ConfirmationModal {...modalConfigs.deleteDocument} />
          <ConfirmationModal {...modalConfigs.deleteRequest} />
          <ConfirmationModal {...modalConfigs.completeRequest} />
          <ConfirmationModal {...modalConfigs.resendRequest} />
        </>
      )}
    </div>
  );
};

DocumentsTab.propTypes = {
  isLoading: PropTypes.bool,
  transactionNumber: PropTypes.string,
  transactionData: PropTypes.object,
  notes: PropTypes.array,
  handleLoadNotes: PropTypes.func,
  handleAddNote: PropTypes.func,
  handleDeleteNote: PropTypes.func,
  handleDownloadDocument: PropTypes.func,
  downloadTransactionDocument: PropTypes.func,
  deleteTransactionDocument: PropTypes.func
};

export default DocumentsTab;
